// src/pages/admin/AdminExpertCircle.jsx
// Back-office : validation des candidatures au cercle des experts (/admin/cercle-experts).
// Critères (décision du 6 oct. 2026) : expérience, diplôme et, surtout, recommandation d'un membre.
// Accepter une candidature envoie l'email de bienvenue ; refuser n'envoie rien (on répond à la main).
// Aucun frais n'est prélevé pendant la candidature ou la création de la fiche.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

const STATUS = { pending: "En attente", accepted: "Accepté", refused: "Refusé" };
const FILTERS = [["pending", "En attente"], ["accepted", "Acceptées"], ["refused", "Refusées"], ["all", "Toutes"]];
const EVENTS = [
  "cercle_page_view", "cercle_apply_submit", "cercle_approved", "expert_profile_complete",
  "expert_first_contact_received", "expert_first_accept", "expert_subscribe", "expert_referral_sent", "expert_referral_applied",
];

const KIND = { sale: "Vente", appraisal: "Estimation, expertise", advice: "Conseil, gestion de collection" };
const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

export default function AdminExpertCircle() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [referrers, setReferrers] = useState({});
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from("expert_circle_applications").select("*").order("created_at", { ascending: false }).limit(200);
    if (filter !== "all") q = q.eq("status", filter);
    const { data, error } = await q;
    if (error) { setMsg({ ok: false, text: "Lecture impossible (réservé aux administrateurs)." }); setRows([]); setLoading(false); return; }
    setRows(data || []);
    const ids = [...new Set((data || []).map((r) => r.referrer_profile_id).filter(Boolean))];
    if (ids.length) {
      const { data: ps } = await supabase.from("profiles").select("id, first_name, last_name, email").in("id", ids);
      setReferrers(Object.fromEntries((ps || []).map((p) => [p.id, p])));
    }
    setLoading(false);
  }, [filter]);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await supabase.from("funnel_events").select("event").in("event", EVENTS).limit(5000);
      const c = {};
      (data || []).forEach((e) => { c[e.event] = (c[e.event] || 0) + 1; });
      setCounts(c);
    } catch { setCounts(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const decide = async (row, decision) => {
    const verb = decision === "accepted" ? "accepter" : "refuser";
    if (!window.confirm(`Confirmer : ${verb} ${row.name} ?${decision === "accepted" && row.mode === "apply" ? " Un email de bienvenue lui sera envoyé." : ""}`)) return;
    setBusy(row.id); setMsg(null);
    const { data, error } = await supabase.functions.invoke("expert-circle-decide", { body: { id: row.id, decision, note: notes[row.id] || "" } });
    setBusy(null);
    if (error || !data?.ok) { setMsg({ ok: false, text: "La décision n'a pas pu être enregistrée." }); return; }
    setMsg({ ok: true, text: decision === "accepted"
      ? (row.mode === "apply" ? (data.welcomeSent ? "Accepté, email de bienvenue envoyé." : "Accepté (l'email de bienvenue n'a pas pu partir : écrivez-lui).") : "Recommandation acceptée : écrivez vous-même au confrère, avec l'accord du recommandant.")
      : "Refusé. Aucun email envoyé : répondez-lui vous-même." });
    load(); loadCounts();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" style={{ fontFamily: "Georgia, serif" }}>
      <h1 className="text-2xl mb-1">Cercle des experts</h1>
      <p className="text-sm text-gray-500 mb-6">Validation des candidatures et recommandations. Critères : expérience, diplôme et, surtout, recommandation d'un membre.</p>

      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6 text-xs">
          {EVENTS.map((e) => (
            <div key={e} className="border rounded-lg bg-white px-3 py-2 flex justify-between"><span className="text-gray-500">{e}</span><strong>{counts[e] || 0}</strong></div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-md text-sm border ${filter === v ? "bg-black text-white border-black" : "bg-white"}`}>{l}</button>
        ))}
      </div>

      {msg && <div className={`mb-4 text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>{msg.text}</div>}
      {loading && <p className="text-sm text-gray-500">Chargement…</p>}
      {!loading && rows.length === 0 && <p className="text-sm text-gray-500">Aucune demande dans cette liste.</p>}

      <div className="grid gap-4">
        {rows.map((r) => {
          const ref = r.referrer_profile_id ? referrers[r.referrer_profile_id] : null;
          const refName = ref ? [ref.first_name, ref.last_name].filter(Boolean).join(" ") || ref.email : null;
          return (
            <div key={r.id} className="border rounded-lg p-4 bg-white">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{r.name} <span className="text-xs font-normal text-gray-500">· {r.mode === "recommend" ? "recommandation d'un confrère" : "candidature"} · {fmt(r.created_at)}</span></div>
                  <div className="text-sm text-gray-600">{[KIND[r.profile_kind], r.house, r.specialty].filter(Boolean).join(" · ") || "—"}</div>
                  <a className="text-sm underline" href={`mailto:${r.email}`}>{r.email}</a>
                  {r.recommended_email && <div className="text-sm text-gray-600">Email du confrère : {r.recommended_email}</div>}
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${r.status === "pending" ? "bg-amber-50 border-amber-200" : r.status === "accepted" ? "bg-green-50 border-green-200" : "bg-gray-100"}`}>{STATUS[r.status]}</span>
              </div>

              <dl className="mt-3 grid md:grid-cols-3 gap-3 text-sm">
                <div><dt className="text-xs text-gray-500">Qui recommande</dt><dd className={refName || r.referrer_text ? "font-medium" : "text-amber-700"}>{refName ? `${refName} (membre)` : r.referrer_text || "Personne indiquée"}</dd></div>
                <div><dt className="text-xs text-gray-500">Expérience</dt><dd>{r.experience || "—"}</dd></div>
                <div><dt className="text-xs text-gray-500">Diplômes</dt><dd>{r.diplomas || "—"}</dd></div>
              </dl>

              {r.status === "pending" ? (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <input value={notes[r.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} placeholder="Note interne (facultatif)" className="flex-1 min-w-[200px] border rounded-md px-3 py-2 text-sm" />
                  <button disabled={busy === r.id} onClick={() => decide(r, "accepted")} className="px-4 py-2 rounded-md bg-black text-white text-sm disabled:opacity-60">Accepter</button>
                  <button disabled={busy === r.id} onClick={() => decide(r, "refused")} className="px-4 py-2 rounded-md border text-sm disabled:opacity-60">Refuser</button>
                </div>
              ) : (
                <div className="mt-3 text-xs text-gray-500">Décidé le {fmt(r.decided_at)}{r.decision_note ? ` · ${r.decision_note}` : ""}{r.welcome_sent_at ? " · bienvenue envoyé" : ""}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
